import type {
    DatapointOptions,
    DatapointTimeGate,
    DatapointTimeGateAdmission,
} from 'owa-analytics-types';
import { isRunningOnWorker } from 'owa-config/lib/isRunningOnWorker';
import { trace } from 'owa-trace';

const HOUR_IN_MILLISECONDS = 3600000;
const MAX_KEYS_PER_HOUR = 4096;
const VALID_KEY = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/;

function isValidPolicy(policy: DatapointTimeGate): boolean {
    return (
        !!policy &&
        policy.oncePer === 'calendarHour' &&
        typeof policy.gateKey === 'string' &&
        VALID_KEY.test(policy.gateKey)
    );
}

/**
 * Page-lifetime authority. A nondecreasing watermark prevents clock rollback from
 * reopening reservations. Never evict keys within an hour: capacity fails closed.
 */
export class CalendarHourGate {
    private hour = -1;
    private readonly keys = new Set<string>();

    reserve(policy: DatapointTimeGate, now: number): boolean {
        if (!isValidPolicy(policy) || !Number.isFinite(now) || now < 0) {
            return false;
        }

        const observedHour = Math.floor(now / HOUR_IN_MILLISECONDS);
        if (!Number.isSafeInteger(observedHour) || observedHour < this.hour) {
            return false;
        }
        if (observedHour > this.hour) {
            this.hour = observedHour;
            this.keys.clear();
        }
        if (this.keys.has(policy.gateKey) || this.keys.size >= MAX_KEYS_PER_HOUR) {
            return false;
        }
        this.keys.add(policy.gateKey);
        return true;
    }
}

let pageGate: CalendarHourGate | undefined;
let workerAdmission: DatapointTimeGateAdmission | undefined;

// Future defaults belong here, shared by routing and enforcement. Currently opt-in only.
export function resolveDatapointTimeGate(options: DatapointOptions): DatapointTimeGate | undefined {
    return options.timeGate;
}

export function reserveDatapointTimeGate(policy: DatapointTimeGate): boolean {
    if (isRunningOnWorker()) {
        return false;
    }
    pageGate ??= new CalendarHourGate();
    return pageGate.reserve(policy, Date.now());
}

export function registerDatapointTimeGateAdmission(
    admission: DatapointTimeGateAdmission | undefined
): void {
    workerAdmission = admission;
}

export function runDatapointTimeGateDiagnostics(...callbacks: (() => void)[]): void {
    for (const callback of callbacks) {
        try {
            callback();
        } catch {
            try {
                trace.warn('Time-gated telemetry diagnostics failed.', 'analytics');
            } catch {
                // Never recurse into telemetry or let a failed warning prevent delivery.
            }
        }
    }
}

export async function admitDatapointTimeGate(policy: DatapointTimeGate): Promise<boolean> {
    if (!isValidPolicy(policy)) {
        return false;
    }
    if (!isRunningOnWorker()) {
        return reserveDatapointTimeGate(policy);
    }
    try {
        // Never retry an ambiguous RPC or authorize locally after losing the page authority.
        return (
            (await workerAdmission?.({
                oncePer: policy.oncePer,
                gateKey: policy.gateKey,
            })) === true
        );
    } catch {
        return false;
    }
}
